import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HStack } from '@chakra-ui/react';
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
export const FIELD_HIGHLIGHTER_CLASSNAMES = {
    JAEN_HIGHLIGHT_FRAME: 'jaen-highlight-frame',
    JAEN_HIGHLIGHT_TOOLTIP: 'jaen-highlight-tooltip'
};
export const FieldHighlighterProviderContext = createContext({
    ref: () => { }
});
const Tooltip = forwardRef((props, ref) => {
    return (_jsx(HStack, { ref: ref, id: "momo", w: "fit-content", mt: "-6", p: "1", mx: "auto", justifyContent: "center", gap: "1", pointerEvents: "all", children: props.actions.map((action, index) => (_jsx(React.Fragment, { children: action }, index))) }));
});
export const FieldHighlighterProvider = props => {
    const fields = useRef([]);
    useEffect(() => {
        fields.current = [];
    }, [props.path]);
    const tooltipHightRef = useRef(null);
    let resizeObserver = useRef(null);
    const setHighlight = (element) => {
        const appRoot = document.getElementById('___gatsby');
        if (!appRoot) {
            alert('appRoot root not found, please contact support');
            return;
        }
        let frameRoot = appRoot.querySelector(`.${FIELD_HIGHLIGHTER_CLASSNAMES.JAEN_HIGHLIGHT_FRAME}`);
        if (frameRoot) {
            frameRoot.remove();
        }
        frameRoot = appRoot.appendChild(document.createElement('div'));
        frameRoot.classList.add(FIELD_HIGHLIGHTER_CLASSNAMES.JAEN_HIGHLIGHT_FRAME);
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        frameRoot.style.position = 'absolute';
        const tooltipRoot = frameRoot.appendChild(document.createElement('div'));
        tooltipRoot.classList.add(FIELD_HIGHLIGHTER_CLASSNAMES.JAEN_HIGHLIGHT_TOOLTIP);
        tooltipRoot.style.position = 'sticky';
        tooltipRoot.style.top = `3.5rem`;
        tooltipRoot.style.pointerEvents = 'none';
        tooltipRoot.style.zIndex = '999';
        tooltipRoot.tabIndex = -1;
        if (resizeObserver.current) {
            resizeObserver.current.disconnect();
        }
        resizeObserver.current = new ResizeObserver(entries => {
            const entry = entries[0];
            if (!entry)
                return;
            const elementRect = entry.target.getBoundingClientRect();
            if (!frameRoot)
                throw new Error('elementRect is null. This should not happen.');
            frameRoot.style.top = `${elementRect.top + scrollTop}px`;
            frameRoot.style.left = `${elementRect.left + scrollLeft}px`;
            frameRoot.style.width = `${elementRect.width}px`;
            frameRoot.style.height = `${elementRect.height}px`;
            tooltipRoot.style.width = '100%';
        });
        resizeObserver.current.observe(element);
        const field = fields.current.find(item => item.ref === element);
        spawnTooltip(field?.tooltipButtons);
    };
    const findClosestParentMatching = (element, find) => {
        let currentElement = element;
        let index = -1;
        while (currentElement) {
            index++;
            if (find(currentElement)) {
                return { element: currentElement, index };
            }
            currentElement = currentElement.parentElement;
        }
        return null;
    };
    const mouseEnterHandler = useCallback((e) => {
        const element = e.target;
        element.focus({
            preventScroll: true
        });
    }, []);
    const mouseLeaveHandler = useCallback((e) => {
        const relatedTarget = e.relatedTarget;
        const nextItem = Object.values(fields.current).find(item => item.ref === relatedTarget);
        if (nextItem) {
            nextItem?.ref?.focus({
                preventScroll: true
            });
        }
        else {
            const closestParent = findClosestParentMatching(relatedTarget, element => fields.current.some(item => item.ref === element));
            if (closestParent) {
                const closestItem = fields.current.find(item => item.ref === closestParent.element);
                closestItem?.ref?.focus({
                    preventScroll: true
                });
            }
        }
    }, []);
    const focusHandler = useCallback((e) => {
        const element = e.target;
        if (element.isContentEditable) {
            const range = document.createRange();
            const selection = window.getSelection();
            if (!selection)
                return;
            range.selectNodeContents(element);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }
        setHighlight(element);
    }, []);
    const blurHandler = useCallback((e) => {
        const highlightRoot = document.querySelector(`.${FIELD_HIGHLIGHTER_CLASSNAMES.JAEN_HIGHLIGHT_FRAME}`);
        if (!highlightRoot)
            return;
        if (tooltipHightRef.current?.contains(e.relatedTarget)) {
            return;
        }
        const element = e.currentTarget;
        if (element?.contains(e.relatedTarget)) {
            return;
        }
        if (highlightRoot.contains(e.relatedTarget)) {
            return;
        }
        highlightRoot.remove();
    }, []);
    const ref = useCallback((ref, tooltipButtons) => {
        if (ref) {
            const fieldIndex = fields.current.findIndex(item => item.ref === ref);
            const field = fields.current[fieldIndex];
            if (field?.ref) {
                field.ref.removeEventListener('mouseenter', mouseEnterHandler);
                field.ref.removeEventListener('mouseleave', mouseLeaveHandler);
                field.ref.removeEventListener('focus', focusHandler);
                field.ref.removeEventListener('blur', blurHandler);
            }
            ref.addEventListener('mouseenter', mouseEnterHandler);
            ref.addEventListener('mouseleave', mouseLeaveHandler);
            ref.addEventListener('focus', focusHandler);
            ref.addEventListener('blur', blurHandler);
            if (fieldIndex !== -1) {
                fields.current[fieldIndex] = {
                    ref,
                    tooltipButtons
                };
            }
            else {
                fields.current.push({
                    ref,
                    tooltipButtons
                });
            }
        }
    }, [
        mouseEnterHandler,
        mouseLeaveHandler,
        focusHandler,
        blurHandler
    ]);
    const [portaledTooltip, setPortaledTooltip] = React.useState();
    const spawnTooltip = useCallback((tooltipButtons = []) => {
        const tooltipRoot = document.querySelector(`.${FIELD_HIGHLIGHTER_CLASSNAMES.JAEN_HIGHLIGHT_TOOLTIP}`);
        if (!tooltipRoot)
            return;
        const portal = createPortal(_jsx(Tooltip, { actions: tooltipButtons, ref: tooltipHightRef }), tooltipRoot);
        setPortaledTooltip(portal);
        return () => {
            setPortaledTooltip(undefined);
        };
    }, []);
    return (_jsxs(FieldHighlighterProviderContext.Provider, { value: { ref }, children: [portaledTooltip, props.children] }));
};
export const useHighlight = ({ tooltipButtons }) => {
    const { ref } = useContext(FieldHighlighterProviderContext);
    const refOnly = useCallback((theRef) => {
        ref(theRef, tooltipButtons);
    }, [tooltipButtons, ref]);
    return {
        ref: refOnly
    };
};
export default FieldHighlighterProvider;
